import React, { useState, useEffect, useCallback } from 'react';
import { useWallet } from '../hooks/useWallet';
import { SigningStargateClient, GasPrice } from '@cosmjs/stargate';

const SWAP_CONTRACT = 'amitis1f6jlx7d9y408tlzue7r2qcf79plp549n30yzqjajjud8vm7m4vdspgrfqp';
const REST = 'https://rest.amitis.network';
const RPC  = 'https://rpc.amitis.network';

export default function Swap() {
  const { address, balance, walletType, nativeWalletRef, REST: ctxRest } = useWallet();

  const [fromAmt,      setFromAmt]      = useState('');
  const [quote,        setQuote]        = useState(null);
  const [contractState,setContractState]= useState(null);
  const [swapping,     setSwapping]     = useState(false);
  const [result,       setResult]       = useState(null);
  const [acceptedDenoms, setAcceptedDenoms] = useState([]);

  // Load contract state and accepted denoms on mount
  useEffect(() => { loadContractState(); }, []);

  async function queryContract(msg) {
    const q = btoa(JSON.stringify(msg));
    const res = await fetch(`${REST}/cosmwasm/wasm/v1/contract/${SWAP_CONTRACT}/smart/${q}`);
    const data = await res.json();
    return data.data;
  }

  async function loadContractState() {
    try {
      const [state, config] = await Promise.all([
        queryContract({ state: {} }),
        queryContract({ config: {} }),
      ]);
      setContractState(state);
      setAcceptedDenoms(config.accepted_denoms || []);
    } catch (e) {
      console.error('loadContractState:', e);
    }
  }

  // Get live quote from contract when amount changes
  useEffect(() => {
    if (!fromAmt || parseFloat(fromAmt) <= 0 || acceptedDenoms.length === 0) {
      setQuote(null);
      return;
    }
    const denom = acceptedDenoms[0]?.denom;
    if (!denom) return;
    const uAmount = Math.floor(parseFloat(fromAmt) * 1_000_000).toString();
    queryContract({ quote: { denom, amount: uAmount } })
      .then(setQuote)
      .catch(() => setQuote(null));
  }, [fromAmt, acceptedDenoms]);

  async function executeSwap() {
    if (!address || !fromAmt || parseFloat(fromAmt) <= 0) return;
    if (acceptedDenoms.length === 0) {
      setResult({ ok: false, msg: 'No accepted input tokens configured yet. Governance must add USDC denom first.' });
      return;
    }

    setSwapping(true);
    setResult(null);

    try {
      const denom   = acceptedDenoms[0].denom;
      const uAmount = Math.floor(parseFloat(fromAmt) * 1_000_000).toString();
      const minOut  = quote ? Math.floor(parseInt(quote.amts_out) * 0.99).toString() : '0'; // 1% slippage

      const msg = {
        typeUrl: '/cosmwasm.wasm.v1.MsgExecuteContract',
        value: {
          sender:   address,
          contract: SWAP_CONTRACT,
          msg:      new TextEncoder().encode(JSON.stringify({ swap: { min_amts_out: minOut } })),
          funds:    [{ denom, amount: uAmount }],
        },
      };

      const fee = {
        amount: [{ denom: 'uamts', amount: '150000' }],
        gas: '300000',
      };

      let txResult;
      if (walletType === 'native' && nativeWalletRef) {
        const client = await SigningStargateClient.connectWithSigner(
          RPC, nativeWalletRef.wallet,
          { gasPrice: GasPrice.fromString('0.025uamts') }
        );
        txResult = await client.signAndBroadcast(address, [msg], fee);
      } else if (walletType === 'keplr' && window.keplr) {
        const signer = window.keplr.getOfflineSigner('amitis-network');
        const client = await SigningStargateClient.connectWithSigner(
          RPC, signer,
          { gasPrice: GasPrice.fromString('0.025uamts') }
        );
        txResult = await client.signAndBroadcast(address, [msg], fee);
      } else {
        throw new Error('No wallet connected');
      }

      if (txResult.code === 0) {
        const amtsOut = quote ? (parseInt(quote.amts_out) / 1_000_000).toFixed(4) : '?';
        setResult({ ok: true, msg: `✓ Swapped ${fromAmt} USDC → ${amtsOut} AMTS`, hash: txResult.transactionHash });
        setFromAmt('');
        setQuote(null);
        loadContractState();
      } else {
        setResult({ ok: false, msg: txResult.rawLog || 'Swap failed' });
      }
    } catch (e) {
      setResult({ ok: false, msg: e.message || 'Swap failed' });
    }
    setSwapping(false);
  }

  const amtsOut    = quote ? (parseInt(quote.amts_out) / 1_000_000).toFixed(4) : '';
  const feeAmt     = quote ? (parseInt(quote.fee_amount) / 1_000_000).toFixed(6) : '';
  const rebateAmt  = quote ? (parseInt(quote.rebate_amount) / 1_000_000).toFixed(6) : '';
  const netFeeAmt  = quote ? (parseInt(quote.net_fee) / 1_000_000).toFixed(6) : '';
  const rateDollars= contractState ? (contractState.current_rate_cents / 100).toFixed(2) : '7.50';
  const reserve    = contractState ? (parseInt(contractState.total_amts_dispensed || 0)).toString() : '—';
  const noDenomsYet= acceptedDenoms.length === 0;

  return (
    <div className="page-content fade-up">
      <div style={{ display: 'grid', gridTemplateColumns: '480px 1fr', gap: 20 }}>
        <div>
          {/* Balance hint */}
          {address && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 10, color: 'var(--dimmer)' }}>
                Available
              </span>
              <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 10, color: 'var(--green)' }}>
                {balance.toLocaleString(undefined, { maximumFractionDigits: 4 })} AMTS
              </span>
            </div>
          )}

          {/* From */}
          <div className="swap-box">
            <div className="swap-label">You Pay</div>
            <div className="swap-row">
              <input
                className="swap-input"
                type="number"
                placeholder="0.00"
                value={fromAmt}
                onChange={e => setFromAmt(e.target.value)}
                disabled={noDenomsYet}
              />
              <div className="swap-token-btn">
                {noDenomsYet ? 'USDC' : (acceptedDenoms[0]?.name || 'USDC')} ▾
              </div>
            </div>
            {fromAmt && <div className="swap-usd-hint">≈ ${parseFloat(fromAmt || 0).toFixed(2)}</div>}
          </div>

          <div className="swap-arrow-row">⇅</div>

          {/* To */}
          <div className="swap-box">
            <div className="swap-label">You Receive</div>
            <div className="swap-row">
              <input
                className="swap-input"
                type="number"
                placeholder="0.00"
                value={amtsOut}
                readOnly
              />
              <div className="swap-token-btn">AMTS</div>
            </div>
            {amtsOut && (
              <div className="swap-usd-hint">≈ ${(parseFloat(amtsOut) * parseFloat(rateDollars)).toFixed(2)}</div>
            )}
          </div>

          {/* Quote details */}
          {quote && fromAmt && (
            <div className="swap-info" style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div className="data-row" style={{ padding: '4px 0' }}>
                <span className="data-label">Rate</span>
                <span className="data-value">1 USDC = {(1 / parseFloat(rateDollars)).toFixed(6)} AMTS</span>
              </div>
              <div className="data-row" style={{ padding: '4px 0' }}>
                <span className="data-label">Fee (0.3%)</span>
                <span className="data-value">{feeAmt} USDC</span>
              </div>
              <div className="data-row" style={{ padding: '4px 0' }}>
                <span className="data-label">80% Rebate</span>
                <span className="data-value green">+{rebateAmt} USDC</span>
              </div>
              <div className="data-row" style={{ padding: '4px 0' }}>
                <span className="data-label">Net Fee</span>
                <span className="data-value gold">{netFeeAmt} USDC</span>
              </div>
              <div className="data-row" style={{ padding: '4px 0' }}>
                <span className="data-label">Slippage tolerance</span>
                <span className="data-value">1%</span>
              </div>
            </div>
          )}

          {/* Status message for no denoms */}
          {noDenomsYet && (
            <div style={{
              background: 'var(--gold-dim)', border: '1px solid var(--gold-border)',
              borderRadius: 10, padding: '12px 14px', marginTop: 10,
              fontFamily: 'DM Mono, monospace', fontSize: 11, color: 'var(--gold)',
            }}>
              Awaiting governance approval to add USDC as accepted input token.
              Once IBC USDC flows to Amitis a governance vote will activate swaps.
            </div>
          )}

          <button
            className="btn btn-primary btn-full"
            style={{ marginTop: 10 }}
            onClick={!address ? () => {} : executeSwap}
            disabled={swapping || noDenomsYet || !fromAmt || parseFloat(fromAmt) <= 0}
          >
            {!address ? 'Connect Wallet to Swap'
              : swapping ? <><div className="spinner"></div> Swapping…</>
              : noDenomsYet ? 'Awaiting Governance Activation'
              : fromAmt && parseFloat(fromAmt) > 0 ? `Swap ${fromAmt} USDC → ${amtsOut || '?'} AMTS`
              : 'Enter amount'}
          </button>

          {result && (
            <div style={{
              marginTop: 12, padding: '12px 14px', borderRadius: 10,
              background: result.ok ? 'var(--green-dim)' : 'var(--red-dim)',
              border: `1px solid ${result.ok ? 'var(--green-border)' : 'rgba(248,113,113,0.25)'}`,
              color: result.ok ? 'var(--green)' : 'var(--red)',
              fontFamily: 'DM Mono, monospace', fontSize: 11,
            }}>
              {result.msg}
              {result.hash && <div style={{ marginTop: 4, opacity: 0.7, fontSize: 10 }}>TX: {result.hash.slice(0,20)}…</div>}
            </div>
          )}
        </div>

        {/* Right panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div className="card-title" style={{ marginBottom: 14 }}>AmitisSwap</div>
            <div className="data-row">
              <span className="data-label">Contract</span>
              <span className="data-value" style={{ fontSize: 10 }}>
                {SWAP_CONTRACT.slice(0,16)}…
              </span>
            </div>
            <div className="data-row">
              <span className="data-label">AMTS Reserve</span>
              <span className="data-value green">1,000,000 AMTS</span>
            </div>
            <div className="data-row">
              <span className="data-label">Current Rate</span>
              <span className="data-value gold">${rateDollars} / AMTS</span>
            </div>
            <div className="data-row">
              <span className="data-label">Floor Price</span>
              <span className="data-value">$7.50 (immutable)</span>
            </div>
            <div className="data-row">
              <span className="data-label">Status</span>
              <span className="data-value green">
                {contractState?.paused ? '⏸ Paused' : '● Active'}
              </span>
            </div>
            <div className="data-row">
              <span className="data-label">Total Swaps</span>
              <span className="data-value">{contractState?.total_swaps ?? '—'}</span>
            </div>
          </div>

          <div className="card">
            <div className="card-title" style={{ marginBottom: 14 }}>Flash Loan Facility</div>
            <div className="data-row">
              <span className="data-label">Available</span>
              <span className="data-value green">21,000,000 AMTS</span>
            </div>
            <div className="data-row">
              <span className="data-label">Fee Rate</span>
              <span className="data-value green">0.05%</span>
            </div>
            <div className="data-row">
              <span className="data-label">vs Aave</span>
              <span className="data-value green">50% cheaper</span>
            </div>
            <button className="btn btn-outline btn-sm btn-full" style={{ marginTop: 14 }}>
              Flash Loan →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
